import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useBranding } from '@/contexts/branding-context';
import { apiRequest } from '@/lib/api-client';
import { Loader2, Upload, X, Building2, Palette } from 'lucide-react';

export function GymBrandingForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { branding, refetch } = useBranding();

  const [logoPreview, setLogoPreview] = useState<string | null>(
    branding?.logo || null,
  );
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState(
    branding?.businessName || '',
  );
  const [primaryColor, setPrimaryColor] = useState(
    branding?.primaryColor || '#C0392B',
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: t('common.error'), description: 'Please select an image file', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: t('common.error'), description: 'Image must be under 5MB', variant: 'destructive' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
      setLogoBase64(result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleClearLogo = () => {
    setLogoPreview(branding?.logo || null);
    setLogoBase64(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let logoUrl = branding?.logo || '';

      // Upload new logo first if one was selected
      if (logoBase64) {
        setUploading(true);
        try {
          const uploadRes = await apiRequest<{ url: string }>({
            method: 'POST',
            url: '/tenants/branding/logo',
            data: { logo: logoBase64 },
          });
          logoUrl = uploadRes.url;
        } finally {
          setUploading(false);
        }
      }

      // Update branding (name + color + logo if not uploaded separately)
      await apiRequest({
        method: 'PATCH',
        url: '/tenants/branding',
        data: {
          businessName: businessName.trim() || undefined,
          primaryColor,
          ...(logoBase64 ? {} : {}), // logo already saved by upload endpoint
        },
      });

      // Refresh branding context so logo/colors update everywhere
      refetch();

      toast({
        title: t('common.success'),
        description: 'Gym settings saved successfully',
      });
      setLogoBase64(null);
      if (logoUrl) setLogoPreview(logoUrl);
    } catch (err) {
      toast({
        title: t('common.error'),
        description: err instanceof Error ? err.message : 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const isBusy = uploading || saving;

  return (
    <div className="space-y-8">
      {/* Logo Upload */}
      <div className="space-y-3">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Gym Logo
        </Label>
        <p className="text-sm text-muted-foreground">
          Upload your gym logo. PNG, JPG, SVG or WebP · Max 5MB
        </p>

        <div
          className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors cursor-pointer
            ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'}`}
          onClick={() => !isBusy && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {logoPreview ? (
            <div className="relative">
              <img
                src={logoPreview}
                alt="Gym logo"
                className="h-24 w-auto max-w-[200px] object-contain rounded-lg"
              />
              {logoBase64 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearLogo(); }}
                  className="absolute -top-2 -right-2 rounded-full bg-destructive p-0.5 text-destructive-foreground hover:bg-destructive/80"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <p className="mt-3 text-center text-xs text-muted-foreground">
                {logoBase64 ? 'New logo selected — click Save to upload' : 'Click or drag to replace'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <div className="rounded-full bg-muted p-4">
                <Upload className="h-6 w-6" />
              </div>
              <span className="text-sm font-medium">Click to upload or drag & drop</span>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Business Name */}
      <div className="space-y-2">
        <Label htmlFor="businessName" className="text-base font-semibold flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Business Name
        </Label>
        <p className="text-sm text-muted-foreground">
          Displayed on the login page and throughout the app
        </p>
        <Input
          id="businessName"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="e.g. Flex Gym"
          className="max-w-sm"
        />
      </div>

      {/* Brand Color */}
      <div className="space-y-2">
        <Label htmlFor="primaryColor" className="text-base font-semibold flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Brand Color
        </Label>
        <p className="text-sm text-muted-foreground">
          Primary color used for buttons, highlights and accents
        </p>
        <div className="flex items-center gap-3">
          <div className="relative h-10 w-10 overflow-hidden rounded-lg border cursor-pointer">
            <input
              id="primaryColor"
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
            <div className="h-full w-full rounded-lg" style={{ backgroundColor: primaryColor }} />
          </div>
          <Input
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            placeholder="#C0392B"
            className="w-32 font-mono text-sm"
            maxLength={7}
          />
          <div className="h-10 w-24 rounded-lg border flex items-center justify-center">
            <span className="text-xs font-medium" style={{ color: primaryColor }}>Preview</span>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="pt-2">
        <Button onClick={handleSave} disabled={isBusy} className="min-w-[120px]">
          {isBusy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {uploading ? 'Uploading...' : t('common.saving')}
            </>
          ) : (
            t('common.save')
          )}
        </Button>
      </div>
    </div>
  );
}
